/**
 * 埋点分析SDK - 核心类
 * 负责事件采集、批量上报、本地队列管理
 */

class AnalyticsSDK {
  constructor(config = {}) {
    // 配置参数
    this.apiUrl = config.apiUrl || '/api/v1/analytics-events/events'
    this.batchApiUrl = config.batchApiUrl || '/api/v1/analytics-events/events/batch'
    this.heatmapApiUrl = config.heatmapApiUrl || '/api/v1/analytics-events/heatmap/data'
    this.heatmapBatchApiUrl = config.heatmapBatchApiUrl || '/api/v1/analytics-events/heatmap/data/batch'
    
    this.batchSize = config.batchSize || 10 // 批量上报大小
    this.batchInterval = config.batchInterval || 5000 // 批量上报间隔(ms)
    this.maxRetry = config.maxRetry || 3 // 最大重试次数
    this.enableHeatmap = config.enableHeatmap !== false // 是否启用热力图
    this.sampleRate = config.sampleRate || 0.2 // 热力图采样率(20%)
    
    // 内部状态
    this.eventQueue = [] // 事件队列
    this.heatmapQueue = [] // 热力图队列
    this.sessionId = this.generateSessionId() // 会话ID
    this.enabled = this.checkConsent() // 是否启用埋点
    this.batchTimer = null // 批量上报定时器
    this.deviceInfo = this.getDeviceInfo() // 设备信息
    
    // 初始化
    this.init()
  }
  
  /**
   * 初始化SDK
   */
  init() {
    if (!this.enabled) {
      console.warn('[Analytics] 用户未同意埋点，SDK已禁用')
      return
    }
    
    // 从本地存储恢复队列
    this.restoreQueue()
    
    // 启动批量上报定时器
    this.startBatchTimer()
    
    // 监听页面卸载事件，上报剩余数据
    window.addEventListener('beforeunload', () => {
      this.flush()
    })
    
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flush()
      }
    })
    
    console.log('[Analytics] SDK初始化完成', {
      sessionId: this.sessionId,
      deviceInfo: this.deviceInfo
    })
  }
  
  /**
   * 生成会话ID
   */
  generateSessionId() {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 15)
    return `${timestamp}-${random}`
  }
  
  /**
   * 检查用户同意
   */
  checkConsent() {
    const consent = localStorage.getItem('analytics_consent')
    if (consent === null) {
      // 默认同意（可根据需求修改）
      localStorage.setItem('analytics_consent', 'true')
      return true
    }
    return consent === 'true'
  }
  
  /**
   * 获取设备信息
   */
  getDeviceInfo() {
    const ua = navigator.userAgent
    
    // 设备类型
    let deviceType = 'desktop'
    if (/mobile/i.test(ua)) {
      deviceType = 'mobile'
    } else if (/tablet/i.test(ua)) {
      deviceType = 'tablet'
    }
    
    // 浏览器
    let browser = 'unknown'
    if (ua.indexOf('Firefox') > -1) {
      browser = 'Firefox'
    } else if (ua.indexOf('Chrome') > -1) {
      browser = 'Chrome'
    } else if (ua.indexOf('Safari') > -1) {
      browser = 'Safari'
    } else if (ua.indexOf('Edge') > -1) {
      browser = 'Edge'
    }
    
    // 操作系统
    let os = 'unknown'
    if (ua.indexOf('Win') > -1) {
      os = 'Windows'
    } else if (ua.indexOf('Mac') > -1) {
      os = 'MacOS'
    } else if (ua.indexOf('Linux') > -1) {
      os = 'Linux'
    } else if (ua.indexOf('Android') > -1) {
      os = 'Android'
    } else if (ua.indexOf('iOS') > -1) {
      os = 'iOS'
    }
    
    // 屏幕分辨率
    const screenResolution = `${window.screen.width}x${window.screen.height}`
    
    return {
      deviceType,
      browser,
      os,
      screenResolution
    }
  }
  
  /**
   * 从本地存储恢复队列
   */
  restoreQueue() {
    try {
      const savedQueue = localStorage.getItem('analytics_event_queue')
      if (savedQueue) {
        this.eventQueue = JSON.parse(savedQueue)
        console.log(`[Analytics] 恢复 ${this.eventQueue.length} 个未上报事件`)
      }
      
      const savedHeatmap = localStorage.getItem('analytics_heatmap_queue')
      if (savedHeatmap) {
        this.heatmapQueue = JSON.parse(savedHeatmap)
          .map((item) => this.normalizeHeatmapItem(item))
          .filter(Boolean)
        console.log(`[Analytics] 恢复 ${this.heatmapQueue.length} 个未上报热力图数据`)
      }
    } catch (error) {
      console.error('[Analytics] 恢复队列失败:', error)
    }
  }
  
  /**
   * 保存队列到本地存储
   */
  saveQueue() {
    try {
      localStorage.setItem('analytics_event_queue', JSON.stringify(this.eventQueue))
      localStorage.setItem('analytics_heatmap_queue', JSON.stringify(this.heatmapQueue))
    } catch (error) {
      console.error('[Analytics] 保存队列失败:', error)
    }
  }
  
  /**
   * 启动批量上报定时器
   */
  startBatchTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
    }
    
    this.batchTimer = setInterval(() => {
      this.flush()
    }, this.batchInterval)
  }
  
  /**
   * 追踪事件
   * @param {string} eventName - 事件名称
   * @param {string} eventCategory - 事件分类
   * @param {object} eventData - 事件数据
   * @param {object} options - 额外选项
   */
  track(eventName, eventCategory = 'custom', eventData = {}, options = {}) {
    if (!this.enabled) return
    
    const event = {
      event_type: eventCategory, // 添加event_type字段
      event_name: eventName,
      event_category: eventCategory,
      event_data: eventData,
      page_url: window.location.pathname,
      page_title: document.title,
      referrer_url: document.referrer,
      session_id: this.sessionId,
      device_type: this.deviceInfo.deviceType,
      browser: this.deviceInfo.browser,
      os: this.deviceInfo.os,
      screen_resolution: this.deviceInfo.screenResolution,
      duration: options.duration || null,
      timestamp: Date.now()
    }
    
    // 添加到队列
    this.eventQueue.push(event)
    
    // 保存到本地存储
    this.saveQueue()
    
    // 如果队列达到批量大小，立即上报
    if (this.eventQueue.length >= this.batchSize) {
      this.flush()
    }
  }
  
  /**
   * 页面访问埋点
   * @param {string} pageUrl - 页面URL
   * @param {object} options - 额外选项
   */
  pageView(pageUrl = window.location.pathname, options = {}) {
    this.track(
      'page_view',
      'page_view',
      {
        entry_point: options.entry_point || 'direct',
        has_heatmap: this.enableHeatmap
      },
      options
    )
    
    // 记录页面进入时间
    this._pageEnterTime = Date.now()
    
    // 如果启用热力图，监听点击事件
    if (this.enableHeatmap) {
      this.startHeatmapTracking()
    }
  }
  
  /**
   * 按钮点击埋点
   * @param {string} buttonName - 按钮名称
   * @param {object} eventData - 事件数据
   */
  buttonClick(buttonName, eventData = {}) {
    this.track(
      buttonName,
      'button_click',
      eventData
    )
  }
  
  /**
   * 表单提交埋点
   * @param {string} formName - 表单名称
   * @param {object} eventData - 事件数据
   */
  formSubmit(formName, eventData = {}) {
    this.track(
      formName,
      'form_submit',
      eventData
    )
  }
  
  /**
   * API调用埋点
   * @param {string} apiName - API名称
   * @param {object} eventData - 事件数据
   */
  apiCall(apiName, eventData = {}) {
    this.track(
      apiName,
      'api_call',
      eventData
    )
  }
  
  /**
   * 规范化热力图点击坐标，避免负值或越界导致后端校验失败
   */
  normalizeHeatmapItem(item) {
    if (!item || !item.page_url) return null

    const viewportWidth = Math.max(1, Number(item.viewport_width) || window.innerWidth || 1)
    const viewportHeight = Math.max(1, Number(item.viewport_height) || window.innerHeight || 1)
    const clickX = Math.round(Number(item.click_x))
    const clickY = Math.round(Number(item.click_y))

    if (!Number.isFinite(clickX) || !Number.isFinite(clickY)) return null

    return {
      ...item,
      page_url: String(item.page_url).slice(0, 500),
      click_x: Math.max(0, Math.min(clickX, viewportWidth - 1)),
      click_y: Math.max(0, Math.min(clickY, viewportHeight - 1)),
      viewport_width: viewportWidth,
      viewport_height: viewportHeight,
      element_selector: item.element_selector
        ? String(item.element_selector).slice(0, 500)
        : null
    }
  }

  /**
   * 开始热力图追踪
   */
  startHeatmapTracking() {
    if (this._heatmapTracking) return
    
    this._heatmapTracking = true
    
    const handleClick = (e) => {
      // 采样控制
      if (Math.random() > this.sampleRate) return
      
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }

      const heatmapData = this.normalizeHeatmapItem({
        page_url: window.location.pathname,
        click_x: e.clientX,
        click_y: e.clientY,
        viewport_width: viewport.width,
        viewport_height: viewport.height,
        element_selector: this.getElementSelector(e.target)
      })

      if (!heatmapData) return
      
      this.heatmapQueue.push(heatmapData)
      this.saveQueue()
      
      // 如果队列达到批量大小，立即上报
      if (this.heatmapQueue.length >= this.batchSize * 2) {
        this.flushHeatmap()
      }
    }
    
    document.addEventListener('click', handleClick, true)
  }
  
  /**
   * 获取元素选择器
   */
  getElementSelector(element) {
    if (!element) return null
    
    // 优先使用ID
    if (element.id) {
      return `#${element.id}`
    }
    
    // 其次使用class
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.trim().split(/\s+/).slice(0, 2).join('.')
      if (classes) {
        return `.${classes}`
      }
    }
    
    // 最后使用标签名
    return element.tagName.toLowerCase()
  }
  
  /**
   * 刷新队列（上报所有待上报数据）
   */
  async flush() {
    await Promise.all([
      this.flushEvents(),
      this.flushHeatmap()
    ])
  }
  
  /**
   * 刷新事件队列
   */
  async flushEvents() {
    if (this.eventQueue.length === 0) return
    
    // 🔧 修复: 如果没有有效token，跳过上报并保留在队列中，不发起请求，避免静默失败+无限重试循环
    if (!this.getToken()) {
      // 限制队列最大长度，防止未登录状态下本地存储无限膨胀
      const maxQueueLimit = 100
      if (this.eventQueue.length > maxQueueLimit) {
        this.eventQueue = this.eventQueue.slice(-maxQueueLimit)
        this.saveQueue()
      }
      return
    }
    
    const eventsToSend = this.eventQueue.splice(0, this.batchSize)
    
    try {
      await this.sendEvents(eventsToSend)
      
      // 清除本地存储
      this.saveQueue()
      
      console.log(`[Analytics] 成功上报 ${eventsToSend.length} 个事件`)
    } catch (error) {
      const msg = error && error.message ? String(error.message) : ''
      const isUnload = msg.includes('Failed to fetch') || msg.includes('Load failed') || msg === 'NetworkError when attempting to fetch resource.'
      
      // 🔧 修复: 如果是 403 或 401 权限问题，或者是 token 失效导致的错误，不应该重新插回队列，应该直接丢弃当前批次，避免无限重试导致不断向后端刷垃圾请求
      const isAuthError = msg.includes('status: 403') || msg.includes('status: 401') || msg.includes('Forbidden') || msg.includes('Unauthorized')
      
      if (isUnload) {
        console.debug('[Analytics] 上报事件跳过/失败(多为页面卸载或网络中断):', msg)
        // 失败的事件重新加入队列
        this.eventQueue.unshift(...eventsToSend)
        this.saveQueue()
      } else if (isAuthError) {
        console.warn('[Analytics] 埋点上报因鉴权失败(403/401)被拦截，丢弃该批次以防止无限循环重试。')
        this.saveQueue()
      } else {
        console.error('[Analytics] 上报事件失败:', error)
        // 失败的事件重新加入队列
        this.eventQueue.unshift(...eventsToSend)
        this.saveQueue()
      }
    }
  }
  
  /**
   * 刷新热力图队列
   */
  async flushHeatmap() {
    if (this.heatmapQueue.length === 0) return
    
    // 🔧 修复: 如果没有有效token，跳过上报并保留在队列中，不发起请求，避免静默失败+无限重试循环
    if (!this.getToken()) {
      // 限制队列最大长度，防止未登录状态下本地存储无限膨胀
      const maxQueueLimit = 200
      if (this.heatmapQueue.length > maxQueueLimit) {
        this.heatmapQueue = this.heatmapQueue.slice(-maxQueueLimit)
        this.saveQueue()
      }
      return
    }
    
    const heatmapToSend = this.heatmapQueue
      .splice(0, this.batchSize * 2)
      .map((item) => this.normalizeHeatmapItem(item))
      .filter(Boolean)

    if (heatmapToSend.length === 0) {
      this.saveQueue()
      return
    }
    
    try {
      await this.sendHeatmapData(heatmapToSend)
      
      // 清除本地存储
      this.saveQueue()
      
      console.log(`[Analytics] 成功上报 ${heatmapToSend.length} 个热力图数据`)
    } catch (error) {
      const msg = error && error.message ? String(error.message) : ''
      const isUnload = msg.includes('Failed to fetch') || msg.includes('Load failed') || msg === 'NetworkError when attempting to fetch resource.'
      
      // 🔧 修复: 如果是 403 或 401 权限问题，丢弃当前批次，避免无限重试死循环
      const isAuthError = msg.includes('status: 403') || msg.includes('status: 401') || msg.includes('Forbidden') || msg.includes('Unauthorized')
      const isValidationError = msg.includes('status: 422') || msg.includes('Unprocessable')
      
      if (isUnload) {
        console.debug('[Analytics] 上报热力图跳过/失败(多为页面卸载或网络中断):', msg)
        // 失败的数据重新加入队列
        this.heatmapQueue.unshift(...heatmapToSend)
        this.saveQueue()
      } else if (isAuthError) {
        console.warn('[Analytics] 热力图上报因鉴权失败(403/401)被拦截，丢弃该批次以防止无限循环重试。')
        this.saveQueue()
      } else if (isValidationError) {
        console.warn('[Analytics] 热力图上报因数据校验失败(422)被丢弃，已清理无效坐标数据')
        this.saveQueue()
      } else {
        console.error('[Analytics] 上报热力图数据失败:', error)
        // 失败的数据重新加入队列
        this.heatmapQueue.unshift(...heatmapToSend)
        this.saveQueue()
      }
    }
  }
  
  /**
   * 发送事件数据
   */
  async sendEvents(events) {
    const token = this.getToken()
    
    // 如果没有有效token，不发送请求，静默失败
    if (!token) {
      console.warn('[Analytics] 未登录或token无效，跳过事件上报')
      // 🔧 修复: 不在这里做 unshift 插入，因为 flushEvents 里已经包含了对 token 的校验。
      return { success: false, reason: 'no_valid_token' }
    }
    
    const response = await fetch(this.batchApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events }),
      keepalive: true
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
  
  /**
   * 发送热力图数据
   */
  async sendHeatmapData(data) {
    const token = this.getToken()
    
    // 如果没有有效token，不发送请求，静默失败
    if (!token) {
      console.warn('[Analytics] 未登录或token无效，跳过热力图数据上报')
      // 🔧 修复: 不在这里做 unshift 插入，因为 flushHeatmap 里已经包含了对 token 的校验。
      return { success: false, reason: 'no_valid_token' }
    }
    
    const response = await fetch(this.heatmapBatchApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ data }),
      keepalive: true
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
  
  /**
   * 获取认证Token
   * 🔧 修复: 使用正确的 token key 'access_token' 并验证token格式
   */
  getToken() {
    // 优先从 localStorage 获取 access_token，然后尝试 token 和 sessionStorage
    const token = localStorage.getItem('access_token') || 
                  localStorage.getItem('token') || 
                  sessionStorage.getItem('access_token') || 
                  sessionStorage.getItem('token') || 
                  ''
    
    // 验证token格式（JWT应该有3个部分，用.分隔）
    if (token && typeof token === 'string') {
      const tokenParts = token.split('.')
      if (tokenParts.length === 3 && token.length > 20) {
        // token格式有效
        return token
      } else {
        console.warn('[Analytics] Token格式无效，将不发送埋点数据')
        return null
      }
    }
    
    return null
  }
  
  /**
   * 检查用户是否已登录
   */
  isUserLoggedIn() {
    const token = this.getToken()
    return token !== null && token !== ''
  }
  
  /**
   * 启用埋点
   */
  enable() {
    this.enabled = true
    localStorage.setItem('analytics_consent', 'true')
    console.log('[Analytics] SDK已启用')
  }
  
  /**
   * 禁用埋点
   */
  disable() {
    this.enabled = false
    localStorage.setItem('analytics_consent', 'false')
    console.log('[Analytics] SDK已禁用')
    
    // 清空队列
    this.eventQueue = []
    this.heatmapQueue = []
    this.saveQueue()
  }
  
  /**
   * 销毁SDK
   */
  destroy() {
    // 上报剩余数据
    this.flush()
    
    // 清除定时器
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
    }
    
    console.log('[Analytics] SDK已销毁')
  }
}

// 导出SDK实例
export default AnalyticsSDK
